import {
  useAddresses,
  useCreateExpressOrder,
  useStoredOrderItems,
  useUserStatistics,
  type StoredItem,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

// ─── Tab type ────────────────────────────────────────────────────────────────
type Tab = "ALL" | "CHECKING" | "STORED" | "HELD";

// ─── Status helpers ───────────────────────────────────────────────────────────
function statusBadgeStyle(status: string): { bg: string; fg: string; dot?: string } {
  switch (status) {
    case "STORED":
      return { bg: "var(--color-primary-soft)", fg: "var(--color-primary-fill)" };
    case "WAIT_STOCK_IN":
    case "SIGNED":
      // 待验货 = yellow
      return { bg: "var(--color-sticker-yellow-soft)", fg: "var(--color-sticker-yellow-fg)" };
    case "RETURNING_GOODS":
    case "EXPIRED":
    case "RELINQUISHED_GOODS":
      // 问题件/待处理 = red
      return { bg: "#FFEEF0", fg: "var(--color-danger)" };
    default:
      return { bg: "var(--color-surface)", fg: "var(--color-text-muted)" };
  }
}

function statusEmoji(status: string): string {
  switch (status) {
    case "STORED":
      return "✓";
    case "WAIT_STOCK_IN":
    case "SIGNED":
      return "📷";
    case "RETURNING_GOODS":
    case "EXPIRED":
    case "RELINQUISHED_GOODS":
      return "⚠";
    default:
      return "";
  }
}

// ─── Total weight helper ──────────────────────────────────────────────────────
function sumWeightKg(items: StoredItem[]): number {
  return items.reduce((acc, it) => acc + (it.weightG ?? 0), 0) / 1000;
}

// ─── Checkbox component ───────────────────────────────────────────────────────
function Checkbox({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? undefined : 0}
      onClick={disabled ? undefined : onChange}
      onKeyDown={disabled ? undefined : (e) => { if (e.key === " " || e.key === "Enter") onChange(); }}
      style={{
        width: 16,
        height: 16,
        border: checked
          ? "1.5px solid var(--color-primary-fill)"
          : "1.5px solid var(--color-border-strong)",
        borderRadius: 4,
        background: checked ? "var(--color-primary-fill)" : "#fff",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.15s",
      }}
    >
      {checked && <span style={{ color: "#fff", fontSize: 9 }}>✓</span>}
    </span>
  );
}

// ─── Item row component ───────────────────────────────────────────────────────
function ItemRow({
  item,
  selected,
  onToggle,
}: {
  item: StoredItem;
  selected: boolean;
  onToggle: () => void;
}) {
  const selectable = item.status === "STORED";
  const badgeStyle = statusBadgeStyle(item.status);
  const emoji = statusEmoji(item.status);
  const weightLabel = item.weightG ? `${(item.weightG / 1000).toFixed(2)} kg` : null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "18px 50px 1fr auto",
        gap: 10,
        padding: "10px 12px",
        borderBottom: "1px solid var(--color-border)",
        alignItems: "center",
        background: selected ? "var(--color-primary-soft)" : "#fff",
        transition: "background 0.15s",
        opacity: !selectable && item.status !== "WAIT_STOCK_IN" && item.status !== "SIGNED" ? 0.7 : 1,
      }}
    >
      <Checkbox checked={selected} onChange={onToggle} disabled={!selectable} />

      {/* Thumbnail */}
      <div
        style={{
          width: 50,
          height: 50,
          borderRadius: 8,
          background: "var(--color-surface)",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          fontSize: 22,
          position: "relative",
        }}
      >
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          "📦"
        )}
        {(item.status === "WAIT_STOCK_IN" || item.status === "SIGNED") && (
          <span
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 14,
              height: 14,
              background: "var(--color-sticker-yellow)",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              fontSize: 8,
            }}
          >
            📷
          </span>
        )}
        {(item.status === "RETURNING_GOODS" || item.status === "EXPIRED" || item.status === "RELINQUISHED_GOODS") && (
          <span
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 14,
              height: 14,
              background: "var(--color-danger)",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              fontSize: 8,
              color: "#fff",
            }}
          >
            !
          </span>
        )}
      </div>

      {/* Info */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.title}
        </div>
        <div style={{ fontSize: 9, color: badgeStyle.fg, fontWeight: 700, marginTop: 2 }}>
          {emoji} {item.statusName}
          {weightLabel && ` · ${weightLabel}`}
        </div>
        {item.orderNo && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--color-text-muted)",
              marginTop: 2,
            }}
          >
            № {item.orderNo}
          </div>
        )}
      </div>

      {/* Price */}
      <div
        style={{
          fontFamily: "-apple-system,system-ui,sans-serif",
          fontSize: 11,
          fontWeight: 800,
          color: "var(--color-price)",
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        {item.priceRubKopecks > 0
          ? formatPrice(item.priceRubKopecks, "ru-RU", "RUB")
          : "—"}
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function Stat({
  color,
  v,
  label,
}: {
  color: "primary" | "cta" | "yellow";
  v: string;
  label: string;
}) {
  const bg = {
    primary: "var(--color-primary-soft)",
    cta: "var(--color-cta-soft)",
    yellow: "var(--color-sticker-yellow-soft)",
  }[color];
  const fg = {
    primary: "var(--color-primary-fill)",
    cta: "var(--color-cta)",
    yellow: "var(--color-sticker-yellow-fg)",
  }[color];
  return (
    <div
      style={{
        background: bg,
        borderRadius: 10,
        padding: "10px 8px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "-apple-system,system-ui,sans-serif",
          fontSize: 18,
          fontWeight: 900,
          color: fg,
        }}
      >
        {v}
      </div>
      <div
        style={{
          fontSize: 9,
          color: "var(--color-text-muted)",
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function WarehousePage() {
  const { t } = useTranslation("warehouse");
  const authToken = useAuthToken();
  const navigate = useNavigate();

  // Fetch both buckets (needed for tab counts and "all" view)
  const ready = useStoredOrderItems(authToken, { isParcelable: "Y", pageNo: 1, pageSize: 50 });
  const held = useStoredOrderItems(authToken, { isParcelable: "N", pageNo: 1, pageSize: 50 });
  const stats = useUserStatistics(authToken);
  const { data: addresses } = useAddresses(authToken);
  const createOrder = useCreateExpressOrder(authToken);

  const [tab, setTab] = useState<Tab>("ALL");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitError, setSubmitError] = useState<string | null>(null);

  const readyRecords = ready.data?.records ?? [];
  const heldRecords = held.data?.records ?? [];

  // All records combined (deduplication not needed — separate API calls with different params)
  const allRecords = [...readyRecords, ...heldRecords];

  // Derive sub-buckets from the combined list
  const checkingRecords = allRecords.filter(
    (r) => r.status === "WAIT_STOCK_IN" || r.status === "SIGNED",
  );
  const storedRecords = readyRecords.filter((r) => r.status === "STORED");
  const heldOnlyRecords = heldRecords;

  const displayRecords: StoredItem[] =
    tab === "ALL" ? allRecords
    : tab === "CHECKING" ? checkingRecords
    : tab === "STORED" ? storedRecords
    : heldOnlyRecords;

  const totalCount = stats.data?.warehouseQuantity ?? allRecords.length;
  const checkingCount = checkingRecords.length;
  // Sum weight of ALL items in warehouse
  const totalWeightKg = sumWeightKg(allRecords);

  // Only STORED items are selectable
  const selectableItems = storedRecords;
  const selectableIds = new Set(selectableItems.map((r) => r.id));

  const toggleItem = (id: string) => {
    if (!selectableIds.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === selectableItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems.map((r) => r.id)));
    }
  };

  const selectedItems = selectableItems.filter((r) => selectedIds.has(r.id));
  const selectedCount = selectedItems.length;
  const selectedWeightKg = sumWeightKg(selectedItems);
  const selectedValueKopecks = selectedItems.reduce((acc, it) => acc + it.priceRubKopecks, 0);

  const isLoading = ready.isLoading || held.isLoading;
  const isError = ready.isError || held.isError;

  const defaultAddr = Array.isArray(addresses) ? addresses.find((a) => a.isDefault) ?? addresses[0] : undefined;

  const handleSubmit = async () => {
    if (selectedCount === 0) return;
    setSubmitError(null);

    if (!defaultAddr) {
      // No address — navigate to address page and let user add one
      navigate({ to: "/address" as never });
      return;
    }

    try {
      const expressOrderNo = await createOrder.mutateAsync({
        orderItemIdList: [...selectedIds],
        orderAddressId: defaultAddr.id,
      });
      // Navigate to submit-parcel with the new express order number
      navigate({
        to: "/submit-parcel" as never,
        search: { expressOrderNo } as never,
      });
    } catch {
      setSubmitError(t("submit.error"));
    }
  };

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "ALL", label: t("tab.all", { n: totalCount }), count: totalCount },
    { key: "CHECKING", label: t("tab.checking", { n: checkingCount }), count: checkingCount },
    { key: "STORED", label: t("tab.ready", { n: storedRecords.length }), count: storedRecords.length },
    { key: "HELD", label: t("tab.held", { n: heldRecords.length }), count: heldRecords.length },
  ];

  const canSubmit = selectedCount > 0 && !createOrder.isPending;

  return (
    <TgPage tabbar>
      <TgHeader
        title={t("head.title")}
        back={false}
        right={
          <Link
            to={"/address" as never}
            style={{
              fontSize: 11,
              color: "var(--color-primary-fill)",
              fontWeight: 700,
              textDecoration: "none",
              padding: "4px 8px",
              borderRadius: 8,
              background: "var(--color-primary-soft)",
            }}
          >
            📍
          </Link>
        }
      />

      <div className="tg-body" style={{ flex: 1, paddingBottom: selectedCount > 0 ? 80 : 64 }}>
        {/* ── Stats row ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
          <Stat
            color="primary"
            v={String(totalCount)}
            label={t("stats.stored")}
          />
          <Stat
            color="cta"
            v={totalWeightKg > 0 ? `${totalWeightKg.toFixed(2)}` : "—"}
            label={t("stats.weight")}
          />
          <Stat
            color="yellow"
            v={String(checkingCount)}
            label={t("stats.checking")}
          />
        </div>

        {/* ── Tab strip ── */}
        <div
          style={{
            display: "flex",
            gap: 4,
            overflowX: "auto",
            padding: "8px 0 2px",
            scrollbarWidth: "none",
          }}
        >
          {tabs.map(({ key, label }) => {
            const on = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                style={{
                  background: on ? "var(--color-text)" : "#fff",
                  color: on ? "#fff" : "var(--color-text-muted)",
                  padding: "5px 10px",
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  border: on ? 0 : "1px solid var(--color-border)",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ── Loading ── */}
        {isLoading && (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            {t("state.loading")}
          </div>
        )}

        {/* ── Error ── */}
        {isError && !isLoading && (
          <div style={{ padding: "24px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "var(--color-danger)", fontWeight: 700 }}>
              {t("state.error_t")}
            </div>
            <button
              type="button"
              onClick={() => { ready.refetch(); held.refetch(); }}
              style={{
                marginTop: 10,
                padding: "6px 18px",
                background: "var(--color-primary-fill)",
                color: "#fff",
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 12,
                border: 0,
                cursor: "pointer",
              }}
            >
              {t("state.error_retry")}
            </button>
          </div>
        )}

        {/* ── Empty ── */}
        {!isLoading && !isError && displayRecords.length === 0 && (
          <TgEmpty
            icon="🏬"
            title={t("empty.title")}
            hint={t("empty.hint")}
            cta={
              <Link
                to={"/catalog" as never}
                style={{
                  display: "inline-block",
                  padding: "8px 18px",
                  background: "var(--color-cta)",
                  color: "#fff",
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                {t("empty.cta")}
              </Link>
            }
          />
        )}

        {/* ── Items list ── */}
        {!isLoading && !isError && displayRecords.length > 0 && (
          <div
            className="tg-card"
            style={{ padding: 0, overflow: "hidden", border: "1.5px solid var(--color-primary-fill)" }}
          >
            {displayRecords.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </div>
        )}

        {/* ── Submit error ── */}
        {submitError && (
          <div
            style={{
              margin: "8px 0",
              padding: "8px 12px",
              background: "#FFEEF0",
              borderRadius: 8,
              fontSize: 11,
              color: "var(--color-danger)",
              fontWeight: 600,
            }}
          >
            {submitError}
          </div>
        )}
      </div>

      {/* ── Bottom sticky bar (replaces TgMain) ── */}
      <div
        style={{
          position: "sticky",
          bottom: 48, // above TgBottomNav
          background: "#0F1F1A",
          color: "#fff",
          padding: "10px 12px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          zIndex: 10,
        }}
      >
        {/* Left: summary + select-all toggle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedCount > 0 ? (
            <>
              <div
                style={{
                  fontSize: 9,
                  color: "rgba(255,255,255,.55)",
                  fontFamily: "monospace",
                }}
              >
                {selectedCount} / {selectableItems.length} · {selectedWeightKg.toFixed(2)} kg
              </div>
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  color: "var(--color-sticker-yellow)",
                }}
              >
                {selectedValueKopecks > 0
                  ? `≈ ${formatPrice(selectedValueKopecks, "ru-RU", "RUB")}`
                  : t("kpi.none_selected")}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.55)", fontFamily: "monospace" }}>
                {t("kpi.none_selected")}
              </div>
              <button
                type="button"
                onClick={toggleAll}
                disabled={selectableItems.length === 0}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,.3)",
                  color: "rgba(255,255,255,.8)",
                  padding: "3px 10px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: selectableItems.length === 0 ? "not-allowed" : "pointer",
                  marginTop: 2,
                }}
              >
                {t("bulk.select_all")}
              </button>
            </>
          )}
        </div>

        {/* Right: submit CTA */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            background: canSubmit ? "var(--color-cta)" : "rgba(255,255,255,.2)",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 12,
            border: 0,
            cursor: canSubmit ? "pointer" : "not-allowed",
            whiteSpace: "nowrap",
            transition: "background 0.2s",
          }}
        >
          {createOrder.isPending ? t("submit.creating") : t("cta_title")}
        </button>
      </div>

      <TgBottomNav />
    </TgPage>
  );
}
